import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Mail, Save, X } from "lucide-react";
import {
  getAuthSession,
  getValidAuthSession,
  updateUserEmail,
  updateUserMetadata,
} from "@/lib/supabaseAuth";

const userTypeOptions = [
  { value: "buscador", label: "Buscador" },
  { value: "propietario", label: "Propietario" },
  { value: "inmobiliaria", label: "Inmobiliaria" },
  { value: "corredor", label: "Corredor" },
];

const EditarDatos = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => getAuthSession());
  const [isSaving, setIsSaving] = useState(false);
  const [isEmailSaving, setIsEmailSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const metadata = session?.user?.user_metadata || {};
  const currentEmail = session?.user?.email || "";
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    let isMounted = true;

    getValidAuthSession().then((currentSession) => {
      if (isMounted) setSession(currentSession);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const [formData, setFormData] = useState({
    nombre:
      metadata.nombre ||
      metadata.full_name ||
      metadata.name ||
      "Usuario Tulocalmx",
    telefono: metadata.telefono || "",
    tipo_usuario: metadata.tipo_usuario || "buscador",
  });

  if (!session?.access_token) {
    navigate("/iniciar-sesion?next=/editar-datos");
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!formData.nombre.trim()) {
      setMessage("Escribe tu nombre para guardar tus datos.");
      return;
    }

    if (!formData.telefono.trim()) {
      setMessage("Escribe tu WhatsApp para que podamos dar seguimiento.");
      return;
    }

    setIsSaving(true);
    try {
      const currentSession = await getValidAuthSession();
      if (!currentSession?.access_token) {
        navigate("/iniciar-sesion?next=/editar-datos");
        return;
      }
      setSession(currentSession);

      const updatedUser = await updateUserMetadata({
        accessToken: currentSession.access_token,
        metadata: {
          ...metadata,
          nombre: formData.nombre.trim(),
          full_name: formData.nombre.trim(),
          telefono: formData.telefono.trim(),
          tipo_usuario: formData.tipo_usuario,
        },
      });

      setSession((current) => ({
        ...current,
        user: updatedUser,
      }));
      setMessage("Tus datos se guardaron correctamente.");
      window.setTimeout(() => navigate("/mi-perfil"), 700);
    } catch (error) {
      setMessage(error.message || "No se pudieron guardar tus datos.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = async (event) => {
    event.preventDefault();
    setEmailMessage("");

    const email = newEmail.trim().toLowerCase();
    if (!email) {
      setEmailMessage("Escribe el nuevo correo.");
      return;
    }

    if (email === currentEmail.toLowerCase()) {
      setEmailMessage("Ese correo ya está en tu cuenta.");
      return;
    }

    setIsEmailSaving(true);
    try {
      const currentSession = await getValidAuthSession();
      if (!currentSession?.access_token) {
        navigate("/iniciar-sesion?next=/editar-datos");
        return;
      }
      setSession(currentSession);

      const updatedUser = await updateUserEmail({
        accessToken: currentSession.access_token,
        email,
        redirectTo: `${window.location.origin}/editar-datos?email_confirmed=1`,
      });

      setSession((current) => ({
        ...current,
        user: updatedUser,
      }));
      setNewEmail("");
      setEmailMessage(
        "Te enviamos un correo de confirmación. El cambio se aplica cuando lo confirmes."
      );
    } catch (error) {
      setEmailMessage(error.message || "No se pudo enviar la confirmación.");
    } finally {
      setIsEmailSaving(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        <section className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Mis datos
              </p>
              <h1 className="mt-2 text-2xl font-black text-slate-950 sm:text-3xl">
                Editar datos de contacto
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Estos datos ayudan a identificar tu cuenta y agilizar el contacto.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/mi-perfil")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Nombre</span>
                <input
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">Correo</span>
                <input
                  value={session.user?.email || "Sin correo"}
                  disabled
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">WhatsApp</span>
                <input
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="+52 667 000 0000"
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Tipo de usuario
                </span>
                <select
                  name="tipo_usuario"
                  value={formData.tipo_usuario}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
                >
                  {userTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {message && (
              <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={isSaving}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0056D2] px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Guardando..." : "Guardar cambios"}
            </button>
          </form>

          <form
            onSubmit={handleEmailChange}
            className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-5"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#0056D2]">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-black text-slate-950">
                  Cambiar correo
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Por seguridad, enviaremos una confirmación al nuevo correo antes de cambiarlo.
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Correo actual
                </span>
                <input
                  value={currentEmail || "Sin correo"}
                  disabled
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-500"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">
                  Nuevo correo
                </span>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  placeholder="nuevo@correo.com"
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
                />
              </label>
            </div>

            {emailMessage && (
              <p className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700">
                {emailMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={isEmailSaving}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Mail className="h-4 w-4" />
              {isEmailSaving ? "Enviando..." : "Enviar confirmación"}
            </button>
          </form>
        </section>
      </main>
    </>
  );
};

export default EditarDatos;
